// src/quality-management/components/project-form/ProjectDetailsGrid.tsx
// Exibe os campos de gestão de um projeto em formato de grade de indicadores

import React from 'react';
import { Project, ProjectStatus } from '../../services/api/types';

const STATUS_LABEL: Record<ProjectStatus, string> = {
  active: 'Em execução',
  on_hold: 'Em espera',
  completed: 'Finalizado',
  cancelled: 'Cancelado',
};

const STATUS_CLASS: Record<ProjectStatus, string> = {
  active: 'bg-brand/10 text-brand',
  on_hold: 'bg-warning/10 text-warning',
  completed: 'bg-success/10 text-success',
  cancelled: 'bg-danger/10 text-danger',
};

const fmtCurrency = (v?: number | null) =>
  v === null || v === undefined
    ? '—'
    : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtPercent = (v?: number | null) =>
  v === null || v === undefined ? '—' : `${v.toLocaleString('pt-BR')}%`;

const fmtNumber = (v?: number | null) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR');

const fmtDate = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

interface FieldProps {
  label: string;
  value: React.ReactNode;
  testId?: string;
}

const Field: React.FC<FieldProps> = ({ label, value, testId }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs font-medium uppercase tracking-wide text-foreground-muted">{label}</span>
    <span className="text-sm font-semibold text-foreground tabular-nums" data-testid={testId}>
      {value}
    </span>
  </div>
);

interface ProgressBarProps {
  label: string;
  value?: number | null;
  tone: 'brand' | 'success';
}

const ProgressBar: React.FC<ProgressBarProps> = ({ label, value, tone }) => {
  const pct = value === null || value === undefined ? null : Math.max(0, Math.min(100, value));
  const bar = tone === 'brand' ? 'bg-brand' : 'bg-success';
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-foreground-muted">{label}</span>
        <span className="text-xs font-semibold text-foreground tabular-nums">{fmtPercent(value)}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
        <div className={`h-full rounded-full ${bar} transition-all`} style={{ width: `${pct ?? 0}%` }} />
      </div>
    </div>
  );
};

interface ProjectDetailsGridProps {
  project: Project;
}

const ProjectDetailsGrid: React.FC<ProjectDetailsGridProps> = ({ project }) => {
  const status = (project.status ?? 'active') as ProjectStatus;
  return (
    <div className="flex flex-col gap-4" data-testid="project-details-grid">
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
        <Field
          label="Nº do projeto"
          value={project.project_number ?? '—'}
          testId="detail-project-number"
        />
        <Field label="Cliente" value={project.client || '—'} testId="detail-client" />
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-medium uppercase tracking-wide text-foreground-muted">Status</span>
          <span
            className={`inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLASS[status]}`}
            data-testid="detail-status"
          >
            {STATUS_LABEL[status]}
          </span>
        </div>
        <Field
          label="PM"
          value={project.pm?.name || '—'}
          testId="detail-pm"
        />
        <Field label="Valor" value={fmtCurrency(project.value)} testId="detail-value" />
        <Field label="Horas" value={fmtNumber(project.hours)} testId="detail-hours" />
        <Field label="Margem" value={fmtPercent(project.margin)} testId="detail-margin" />
        <Field label="PPI" value={fmtNumber(project.ppi)} testId="detail-ppi" />
        <Field label="Tmp. restante" value={project.remaining_time != null ? `${fmtNumber(project.remaining_time)} h` : '—'} testId="detail-remaining-time" />
        <Field label="Início" value={fmtDate(project.start_date)} testId="detail-start-date" />
        <Field label="Fim" value={fmtDate(project.end_date)} testId="detail-end-date" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ProgressBar label="Progresso técnico" value={project.technical_progress} tone="brand" />
        <ProgressBar label="Progresso financeiro" value={project.financial_progress} tone="success" />
      </div>
    </div>
  );
};

export default ProjectDetailsGrid;
